import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Header from './components/Header';
import Hero from './components/Hero';
import Marquee from './components/Marquee';
import About from './components/About';
import ProductsGrid from './components/ProductsGrid';
import OilsShowcase from './components/OilsShowcase';
import Process from './components/Process';
import CtaBanner from './components/CtaBanner';
import Footer from './components/Footer';
import Seo, { SITE_URL } from './components/Seo';
import { HOME_DESCRIPTION, HOME_TITLE, SITE_KEYWORDS } from './config/seoKeywords';
import './Home.css';

gsap.registerPlugin(ScrollTrigger);

function Home() {
  const rootRef = useRef(null);
  const description = HOME_DESCRIPTION;
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${SITE_URL}/#webpage`,
    url: SITE_URL,
    name: HOME_TITLE,
    description,
    inLanguage: 'en-GB',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#organization` },
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      heroTl
        .from('.hero-bg-img', { scale: 1.12, duration: 2.2, ease: 'power2.out' })
        .from('.site-header > *', { y: -24, opacity: 0, duration: 0.7, stagger: 0.06 }, '-=1.8')
        .from('.hero-line', { y: 70, opacity: 0, duration: 0.85, stagger: 0.14 }, '-=1.2')
        .from('.hero-badge, .hero-divider, .hero-subtitle, .hero-ctas, .hero-stats', {
          y: 32,
          opacity: 0,
          duration: 0.7,
          stagger: 0.12,
        }, '-=0.5')
        .from('.hero-circle-text', { opacity: 0, rotation: -30, duration: 1.2 }, '-=1.4')
        .from('.hero-scroll-hint', { opacity: 0, y: 16, duration: 0.6 }, '-=0.3');

      gsap.utils.toArray('.reveal').forEach((el) => {
        if (el.closest('.hero') || el.closest('.about-visual')) return;

        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
          y: 48,
          opacity: 0,
          duration: 0.75,
          ease: 'power3.out',
        });
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="page" ref={rootRef}>
      <Seo
        title={HOME_TITLE}
        description={description}
        keywords={SITE_KEYWORDS}
        schema={schema}
      />
      <Header />
      <main className="main">
        <Hero />
        <Marquee />
        <About />
        <ProductsGrid />
        <OilsShowcase />
        <Process />
        <CtaBanner />
      </main>
      <Footer />
    </div>
  );
}

export default Home;
